import {useState} from 'react';
import type {ReactElement, ReactNode} from 'react';
import type {TooltipProps} from 'antd';
import styles from './PaywallTooltip.module.less';
import Tooltip from '@/components/common/Tooltip';
import {useTracking} from '@/services/tracking/context';
import {TrackEvents} from '@/services/tracking/constant';
import {useMemberLogExt} from '@/hooks/memberLogExt';
import type {LogExt} from '@/services/tracking';

interface PaywallTooltipProps {
    /** 提示文案 */
    hint?: string;
    /** CTA 按钮文案（如 "开通会员"） */
    ctaText?: string;
    /** CTA 点击回调 */
    onCta?: () => void;
    /** 付费墙触发场景（对应 BenefitKey，如 'feature_script'） */
    scene?: string;
    /** CTA 类型：subscribe / upgrade */
    ctaType?: string | null;
    /** 额外的埋点 ext 字段（如 template_id、template_type） */
    extraExt?: LogExt;
    /** Tooltip 弹出位置 */
    placement?: TooltipProps['placement'];
    align?: TooltipProps['align'];
    children: ReactElement;
}

/** 付费墙 Tooltip：支持纯文案 / 文案 + CTA 按钮 */
export function PaywallTooltip({
    hint,
    ctaText,
    onCta,
    scene,
    ctaType,
    extraExt,
    placement,
    align,
    children,
}: PaywallTooltipProps) {
    const [open, setOpen] = useState(false);
    const {sendClickLog} = useTracking();
    const memberLogExt = useMemberLogExt();

    if (!hint) return children;

    const title: ReactNode =
        ctaText && onCta ? (
            <span className={styles.content}>
                <span>{hint}</span>
                <a
                    className={styles.cta}
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                        sendClickLog({
                            value: TrackEvents.MEMBER_TOOLTIP_CTA,
                            ext: {
                                /* eslint-disable camelcase */
                                ...(scene ? {scene} : {}),
                                ...(ctaType ? {cta_type: ctaType} : {}),
                                ...memberLogExt,
                                ...extraExt,
                                /* eslint-enable camelcase */
                            },
                        });
                        onCta();
                    }}
                >
                    {ctaText}
                </a>
            </span>
        ) : (
            hint
        );

    return (
        <Tooltip
            title={title}
            placement={placement}
            align={align}
            styles={{root: {maxWidth: 'none'}}}
            open={open}
            onOpenChange={setOpen}
        >
            {children}
        </Tooltip>
    );
}
